import React from 'react';
import styled, { css } from 'styled-components';

import {
  Box, Button, TextInput, TextArea, CheckBox,
} from 'grommet';
import { DropButton } from '@Components/Control';

const StyledFormFieldContainer = styled(Box)`
  border-radius: var(--app-radius);
  background: ${({ bgColor }) => bgColor ?? 'var(--page-background)'};
  box-shadow: var(--elevation);
`;

const StyledFormSelect = styled((props) => <DropButton {...props} />)`
  width: 100%;
  box-shadow: ${({ noShadow }) => (noShadow ? 'none' : 'var(--elevation)')};
  border-radius: var(--app-radius);

  > button {
    background: ${({ bgColor }) => bgColor ?? 'var(--page-input-bg)'};
    font-size: 0.8rem;
    padding: ${({ customPad }) => customPad || '0.5rem 1rem'};
    border-radius: var(--app-radius);
    border: ${({ borderColor }) => (borderColor ? `1px solid ${borderColor}` : '1px solid var(--page-input-border)')};
    border-color: ${({ isOpen, highlight }) => (isOpen ? highlight : 'var(--page-input-border)')};
    width: 100%;

    &:focus {
      border: ${({ highlight }) => `1px solid ${highlight}`};
    }

    > div {
      border-radius: var(--app-radius);
      width: 100%;
      -webkit-box-pack: justify;
      -ms-flex-pack: justify;
      justify-content: space-between;
    }
  }
`;

const StyledFormSelectMenu = styled(Button)`
  padding: 0.75rem;
  border-radius: var(--app-radius);

  &:hover {
    color: ${({ highlightText }) => highlightText};
  }
`;

const StyledModalActivateButton = styled(Button)`
  width: 100%;
  background-color: var(--page-input-bg);
  border-radius: var(--app-radius);
  border: ${({ modalOpen, highlight, borderColor }) => (modalOpen ? `1px solid ${highlight}` : `1px solid ${borderColor}`)};
  padding: ${({ lessPad }) => (lessPad ? '0.5rem 1rem' : '0.65rem 1rem')};

  img {
    max-width: 18px;
  }
`;

const StyledModalSelectButton = styled(Button)`
  width: ${({ customWidth }) => (customWidth || '10.5rem')};
  height: ${({ customHeight }) => (customHeight || '2.85rem')};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--app-radius);
  border: ${({ borderColor }) => (borderColor ? `1px solid ${borderColor}` : '1px solid var(--page-input-border)')};
  color: ${({ primaryText }) => primaryText};
  background-color: ${({ bgColor }) => bgColor ?? 'var(--page-input-bg)'};

  &:hover {
    border: ${({ buttonHighlight }) => `1px solid ${buttonHighlight}`};
    color: ${({ buttonHighlight }) => buttonHighlight};
    background-color: ${({ buttonBg }) => buttonBg};
  }

  ${({ selected, buttonHighlight, buttonBg }) => selected && `
    border: ${`1px solid ${buttonHighlight}`};
    color: ${buttonHighlight};
    background-color: ${buttonBg};
  `}

  @media (max-width: 768px) {
    width: 8.75rem;
  }
`;

const StyledRaisedContainer = styled(Box)`
  border-radius: var(--app-radius);
  box-shadow: var(--elevation);
  background-color: ${({ bgColor }) => bgColor ?? 'var(--page-card-bg)'};
`;

const StyledFormInputClear = styled(Button)`
  position: absolute;
  padding: 0.5rem;
  top: 0;
  bottom: 0;
  right: 0;
`;

const inputStyle = css`
  background: ${({ bgColor }) => bgColor ?? 'var(--page-input-bg)'};
  font-size: 0.8rem;
  border: ${({ borderColor }) => (borderColor ? `solid 1px ${borderColor}` : 'solid 1px var(--page-input-border)')};
  padding: 0.65rem 1rem;
  border-radius: var(--app-radius);
  width: 100%;

  &:focus {
    border-color: ${({ highlight }) => highlight};
  }
`;

const StyledFormInput = styled(TextInput)`${inputStyle}`;

const StyledFormTextArea = styled(TextArea)`${inputStyle}`;

const StyledUploadContainer = styled(Box)`
  position: relative;

  * { cursor: pointer; }

  &:hover > button {
    box-shadow: none;
    color: var(--button-primary-text);
    border: ${({ highlight }) => `1px solid ${highlight}`};
  }
`;

const StyledUploadInput = styled.input`
  position: absolute;
  opacity: 0;
  height: 100%;
  width: 100%;
`;

const StyledFormCloseContainer = styled(Box)`
  svg { padding-top: 0.1rem };

  &:hover {
    span {
      color: ${({ highlight }) => highlight};
    }

    svg {
      stroke: ${({ highlight }) => highlight};
      fill: ${({ highlight }) => highlight};
    }
  }
`;

const StyledSmallButton = styled(Button)`
  backface-visibility: hidden;

  &:disabled { opacity: 1; }
  &:hover:not(:disabled) > div {
    border: ${({ highlight }) => `1px solid ${highlight}`};
  }

  &:focus:not(:disabled) > div {
    border: ${({ highlight }) => `1px solid ${highlight}`};
  }
`;

const StyledCheckBox = styled(CheckBox)`
  backface-visibility: hidden;

  + div {
    background-color: ${({ bgColor }) => bgColor ?? 'var(--page-input-bg)'};
    border: ${({ borderColor }) => (borderColor ? `1px solid ${borderColor}` : '1px solid var(--page-input-border)')};
    height: ${({ customBoxSize }) => (customBoxSize || '1.75rem')};
    width: ${({ customBoxSize }) => (customBoxSize || '1.75rem')};
  }

  &:hover + div {
    border: ${({ focusHighlight }) => `1px solid ${focusHighlight}`} !important;
  }

  + div > svg {
    stroke: ${({ checked, color }) => (checked ? color : 'transparent')};
    width: ${({ customCheckmarkSize }) => (customCheckmarkSize || '1.45rem')};
    height: ${({ customCheckmarkSize }) => (customCheckmarkSize || '1.45rem')};
  }
`;

export {
  StyledFormFieldContainer,
  StyledFormSelectMenu,
  StyledFormSelect,
  StyledRaisedContainer,
  StyledFormInput,
  StyledFormInputClear,
  StyledModalActivateButton,
  StyledModalSelectButton,
  StyledFormCloseContainer,
  StyledFormTextArea,
  StyledSmallButton,
  StyledUploadContainer,
  StyledUploadInput,
  StyledCheckBox,
};
